import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  User,
  UserCredential,
  Auth
} from 'firebase/auth';
import { initializeFirebase } from '../firebase';
import { AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const { auth: firebaseAuth } = await initializeFirebase();
        setAuth(firebaseAuth);

        const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
          setCurrentUser(user);
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Firebase initialization error:', error);
        setInitError((error as Error).message);
        setLoading(false);
      }
    };

    init();
  }, []);

  const signup = useCallback(async (email: string, password: string): Promise<UserCredential> => {
    if (!auth) throw new Error('Firebase not initialized');
    return createUserWithEmailAndPassword(auth, email, password);
  }, [auth]);

  const login = useCallback(async (email: string, password: string): Promise<UserCredential> => {
    if (!auth) throw new Error('Firebase not initialized');
    return signInWithEmailAndPassword(auth, email, password);
  }, [auth]);

  const logout = useCallback(async (): Promise<void> => {
    if (!auth) throw new Error('Firebase not initialized');
    // Clear welcome shown flag so it shows again on next login
    sessionStorage.removeItem('kids_welcome_shown_session');
    return signOut(auth);
  }, [auth]);

  const signInWithGoogle = useCallback(async (): Promise<UserCredential> => {
    if (!auth) throw new Error('Firebase not initialized');
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }, [auth]);

  const value: AuthContextType = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    signInWithGoogle,
    initError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
